import React, { useEffect, useId, useRef, useState } from 'react';
import useListboxNav from '../hooks/useListboxNav';

/**
 * The shared typeahead. Every school / college box should be built on this.
 *
 * It follows the W3C APG "combobox with listbox popup" pattern, which
 * Downshift's useCombobox and React Aria's ComboBox also implement:
 *
 *  - Typed text and the chosen item are separate. Typing calls
 *    `onInputChange` and NEVER `onSelect`; a pick (click, or Enter on a
 *    highlighted row) calls `onSelect` and never `onInputChange`.
 *  - The input is never trimmed or rewritten: `value` is exactly what the
 *    caller holds, so a space can always be typed.
 *  - Rows pick on click while `mousedown` is prevented, so the input never
 *    loses focus mid-pick. That removes the blur-then-click race the older
 *    hand-rolled boxes worked around with 200ms timers.
 *  - Free text is committed only through `onCommitText`, called once when the
 *    field is left with text that differs from what it started with (and not
 *    right after a pick). A caller that does not want free text omits it.
 *  - Keyboard: ArrowDown/ArrowUp move a highlight (ArrowDown opens a closed
 *    list), Enter picks the highlighted row only, Escape closes. Enter with
 *    nothing highlighted is left alone so a surrounding form can submit.
 *  - ARIA: role=combobox with aria-expanded / aria-controls /
 *    aria-autocomplete / aria-activedescendant, a role=listbox popup, and
 *    role=option rows.
 *
 * The component owns no data: the caller searches and passes `items`.
 *
 * @param {object}   props
 * @param {string}   props.value                 the input text, verbatim
 * @param {(text: string) => void} props.onInputChange   typing
 * @param {Array}    props.items                 current suggestions
 * @param {(item) => string|number} props.getKey
 * @param {(item) => React.ReactNode} props.renderItem
 * @param {(item) => void} props.onSelect        an explicit pick
 * @param {(text: string) => void} [props.onCommitText]  free text, on leaving
 * @param {React.ReactNode} [props.footer]       a note under the list (e.g.
 *                                               "showing the first 100")
 * @param {object}   [props.classNames]          root, input, popup, list,
 *                                               option, optionActive, footer
 */
export default function Combobox({
  value,
  onInputChange,
  items,
  getKey,
  renderItem,
  onSelect,
  onCommitText,
  footer,
  placeholder,
  disabled,
  ariaLabel,
  autoFocus,
  classNames = {},
  rootStyle,
  inputProps: inputPropsIn = {},
  children, // rendered after the input inside the root (e.g. a clear button)
}) {
  const { onKeyDown: extraKeyDown, ...inputProps } = inputPropsIn;
  const baseId = useId();
  const listId = `${baseId}-list`;
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  // Text at the moment the field was entered: blur commits only a real change.
  const startText = useRef(value);
  // True from a pick until the next keystroke, so the blur that follows a pick
  // does not commit the half-typed text over it.
  const justPicked = useRef(false);

  const list = items || [];
  const expanded = open && list.length > 0;

  const pick = (item) => {
    justPicked.current = true;
    setOpen(false);
    reset();
    onSelect(item);
  };

  const { highlight, setHighlight, reset, onKeyDown: navKeyDown } = useListboxNav({
    count: list.length,
    open: expanded,
    setOpen,
    onPick: (i) => pick(list[i]),
  });

  useEffect(() => {
    const onDocDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  const optionId = (i) => `${baseId}-opt-${i}`;

  const handleChange = (e) => {
    justPicked.current = false;
    reset();
    setOpen(true);
    onInputChange(e.target.value);
  };

  const handleFocus = () => {
    startText.current = value;
    if (list.length > 0 && (value || '').length > 0) setOpen(true);
  };

  const handleBlur = () => {
    setOpen(false);
    reset();
    if (!onCommitText || justPicked.current) return;
    if ((value || '') !== (startText.current || '')) {
      startText.current = value;
      onCommitText(value);
    }
  };

  return (
    <div ref={rootRef} className={classNames.root} style={{ position: 'relative', ...rootStyle }}>
      <input
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={expanded}
        aria-controls={expanded ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={expanded && highlight >= 0 ? optionId(highlight) : undefined}
        className={classNames.input}
        value={value || ''}
        onChange={handleChange}
        onKeyDown={(e) => {
          navKeyDown(e);
          // Caller's own key handling (e.g. Escape closes a surrounding picker) runs
          // only when the combobox did not use the key.
          if (!e.defaultPrevented && extraKeyDown) extraKeyDown(e);
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        autoFocus={autoFocus}
        {...inputProps}
      />
      {children}
      {expanded && (
        <div className={classNames.popup}>
          <ul id={listId} role="listbox" className={classNames.list} style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {list.map((item, i) => (
              <li
                key={getKey(item)}
                id={optionId(i)}
                role="option"
                aria-selected={i === highlight}
                className={[classNames.option, i === highlight ? classNames.optionActive : '']
                  .filter(Boolean).join(' ')}
                // preventDefault keeps focus in the input, so a pick never
                // races a blur.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(item)}
                onMouseEnter={() => setHighlight(i)}
              >
                {renderItem(item)}
              </li>
            ))}
          </ul>
          {footer && <div role="note" className={classNames.footer}>{footer}</div>}
        </div>
      )}
    </div>
  );
}
